import React, { useState } from "react";
import { find } from "lodash";
import styled from "styled-components";
import { content } from "@glossy";
import { useDebouncedEffect } from "@lkf/hooks";
import TextAutoGrow from "@ui-kit/form/textarea/autogrow";
import PanelComponent from "@ui-kit/popover/panel";
import SafeHTML from "@ui-kit/safe-html";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import { isClauseEmpty, isDocIdsClause } from "@radarly/models/clause/is";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { FullFacet } from "@radarly/models/facets";

interface IProps {
  clause: Clause;
  setClause: (clause: Clause) => void;
  facet: FullFacet;
  onApply?: () => void;
  onCancel?: () => void;
  validateLabel?: string;
  className?: string;
}

const Description = styled.div`
  margin-bottom: 24px;
  color: ${(props) => content(props, "secondary")};
`;

function getDocIdsValue(clause: Clause) {
  if (clause && clause.and) {
    const docIdsClause = find(clause.and, isDocIdsClause);
    return docIdsClause ? docIdsClause.docIds.in.toString() : "";
  } else {
    return "";
  }
}

export default function TextPanelContainer({
  clause,
  setClause,
  facet,
  onApply,
  onCancel,
  validateLabel,
  className,
}: IProps) {
  const locale = useCurrentUserLocale();
  const [ids, setIds] = useState(getDocIdsValue(clause));

  useDebouncedEffect(
    () => {
      setClause(
        refineClauseWithInnerClause(clause, {
          [facet.id]: {
            in: ids.length > 0 ? ids.replace(/\s/g, "").split(",") : [],
            not: [],
          },
        })
      );
    },
    1000,
    [ids]
  );

  const onClear = () => {
    setIds("");
    setClause(
      refineClauseWithInnerClause(clause, {
        [facet.id]: { in: [], not: [] },
      })
    );
  };

  return (
    <PanelComponent
      locale={locale}
      onClear={onClear}
      hasTitle
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      onApply={onApply}
      disabled={ids === "" || (onCancel && isClauseEmpty(clause))}
      onCancel={onCancel}
      validateLabel={validateLabel}
      className={className}
    >
      {facet.description && (
        <Description>
          <SafeHTML html={facet.description} />
        </Description>
      )}
      <TextAutoGrow
        value={ids}
        onChange={(value) => setIds(value)}
        maxHeight={200}
      />
    </PanelComponent>
  );
}
